/**
 * Шаг 3.1 wizard «Экскурсия» (E07.4, ФТ-1.4; E15 выдача файлов).
 * Только для «Равный диалог» и «Эксперт»: последовательность окон
 * с изображениями, 3D и референсами.
 */

import { useEffect, useMemo, useState } from "react";
import {
  API_V1_PREFIX,
  TOUR_MEDIA_TYPE_LABELS,
  publicFilePath,
  type PublicTourSlide,
  type TourMediaType,
} from "@potok/shared";
import type { PublicCatalogApi } from "../../api/catalog.api";
import { ApiRequestError } from "../../api/http";

export type TourStepProps = {
  api: PublicCatalogApi;
  industryId: string | null;
  onBack: () => void;
  onContinue: () => void;
  onSkip: () => void;
};

/** Каркас типов медиа, если в отрасли ещё нет загруженных материалов. */
const FALLBACK_SLIDES: PublicTourSlide[] = [
  {
    id: "00000000-0000-4000-8000-000000000001",
    mediaType: "image",
    modelId: null,
    modelName: null,
    fileId: "00000000-0000-4000-8000-000000000011",
    title: "Изображения оборудования",
    sortOrder: 10,
  },
  {
    id: "00000000-0000-4000-8000-000000000002",
    mediaType: "media_3d",
    modelId: null,
    modelName: null,
    fileId: "00000000-0000-4000-8000-000000000012",
    title: "3D-модель",
    sortOrder: 20,
  },
  {
    id: "00000000-0000-4000-8000-000000000003",
    mediaType: "reference",
    modelId: null,
    modelName: null,
    fileId: "00000000-0000-4000-8000-000000000013",
    title: "Референсные объекты",
    sortOrder: 30,
  },
];

export function TourStep({ api, industryId, onBack, onContinue, onSkip }: TourStepProps) {
  const [slides, setSlides] = useState<PublicTourSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setIndex(0);

    api
      .listTour(industryId)
      .then((loaded) => {
        if (cancelled) {
          return;
        }
        if (loaded.length === 0) {
          setSlides(FALLBACK_SLIDES);
          setUsingFallback(true);
        } else {
          setSlides(loaded);
          setUsingFallback(false);
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(describeError(caught));
          setSlides(FALLBACK_SLIDES);
          setUsingFallback(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, industryId]);

  const current = slides[index] ?? null;
  const isLast = slides.length > 0 && index >= slides.length - 1;

  const progressLabel = useMemo(() => {
    if (slides.length === 0) {
      return "";
    }
    return `${index + 1} из ${slides.length}`;
  }, [index, slides.length]);

  return (
    <section className="wizard-step" aria-labelledby="tour-step-title">
      <h2 id="tour-step-title">Экскурсия</h2>
      <p className="wizard-step__hint">
        Краткий обзор материалов по выбранной отрасли: изображения, 3D и референсы. Можно
        пропустить и перейти к параметрам.
      </p>

      {error ? (
        <p className="wizard-step__status wizard-step__status--error" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? <p className="wizard-step__hint">Загрузка экскурсии…</p> : null}

      {!loading && current ? (
        <div className="tour-stage">
          <p className="tour-stage__progress" aria-live="polite">
            {progressLabel}
            {usingFallback ? " · контент появится после загрузки медиа" : null}
          </p>
          <article
            className="tour-slide"
            data-media-type={current.mediaType}
            aria-label={current.title}
          >
            <TourMediaPanel
              mediaType={current.mediaType}
              title={current.title}
              fileId={current.fileId}
              showFile={!usingFallback}
            />
            <h3 className="tour-slide__title">{current.title}</h3>
            <p className="tour-slide__meta">
              {TOUR_MEDIA_TYPE_LABELS[current.mediaType]}
              {current.modelName ? ` · ${current.modelName}` : null}
            </p>
          </article>
        </div>
      ) : null}

      <div className="wizard-step__actions">
        <button type="button" onClick={onBack}>
          Назад
        </button>
        <button type="button" onClick={onSkip}>
          Пропустить экскурсию
        </button>
        <button
          type="button"
          disabled={loading || current === null || index === 0}
          onClick={() => setIndex((value) => Math.max(0, value - 1))}
        >
          Предыдущий
        </button>
        {isLast ? (
          <button
            type="button"
            className="wizard-step__primary"
            disabled={loading || current === null}
            onClick={onContinue}
          >
            Далее
          </button>
        ) : (
          <button
            type="button"
            className="wizard-step__primary"
            disabled={loading || current === null}
            onClick={() => setIndex((value) => Math.min(slides.length - 1, value + 1))}
          >
            Следующий
          </button>
        )}
      </div>
    </section>
  );
}

function TourMediaPanel({
  mediaType,
  title,
  fileId,
  showFile,
}: {
  mediaType: TourMediaType;
  title: string;
  fileId: string;
  showFile: boolean;
}) {
  const src = `/${API_V1_PREFIX}${publicFilePath(fileId)}`;

  return (
    <div className="tour-slide__panel" role="img" aria-label={title}>
      <span className="tour-slide__badge">{TOUR_MEDIA_TYPE_LABELS[mediaType]}</span>
      {mediaType === "image" && showFile ? (
        <img className="tour-slide__image" src={src} alt={title} />
      ) : mediaType === "media_3d" ? (
        <p className="tour-slide__placeholder">
          {showFile
            ? "3D-модель доступна для скачивания; просмотр R3F — следующий срез"
            : "Просмотр 3D появится после загрузки медиа"}
        </p>
      ) : mediaType === "reference" && showFile ? (
        <p className="tour-slide__placeholder">
          <a href={src} target="_blank" rel="noreferrer">
            Открыть референс
          </a>
        </p>
      ) : mediaType === "image" ? (
        <p className="tour-slide__placeholder">Изображение будет показано после загрузки файла</p>
      ) : mediaType === "reference" ? (
        <p className="tour-slide__placeholder">Референсный материал будет доступен после загрузки</p>
      ) : (
        <p className="tour-slide__placeholder">Текстовый материал</p>
      )}
    </div>
  );
}

function describeError(caught: unknown): string {
  if (caught instanceof ApiRequestError) {
    return caught.message;
  }
  if (caught instanceof Error) {
    return caught.message;
  }
  return "Не удалось загрузить экскурсию";
}
